import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Calculator,
  Coins,
} from 'lucide-react';

const CashDrawerModal = ({
  isOpen,
  user,
  onConfirm,
  onError,
  lastAmount = 0,
}) => {
  const [rawAmount, setRawAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreset, setShowPreset] = useState(false);

  const presetAmounts = [0, 5000, 10000, 15000, 20000, 25000, 50000, 100000];

  useEffect(() => {
    if (!isOpen) return;
    // Par défaut, ne pas pré-remplir avec la clôture précédente :
    // ça évite de démarrer une nouvelle session avec un gros montant par erreur.
    setRawAmount('');
    setError('');
    setShowPreset(false);
  }, [isOpen, lastAmount]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const handleAmountChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^\d]/g, '');
    setRawAmount(digitsOnly);
    setError('');
  };

  const handlePresetSelect = (amount) => {
    setRawAmount(Math.max(0, amount).toString());
    setShowPreset(false);
    setError('');
  };

  const handleStartWithEmpty = async () => {
    setIsLoading(true);
    setError('');
    try {
      await onConfirm(0, 'Démarrage avec tiroir vide');
      setRawAmount('');
    } catch (err) {
      const serverMessage =
        err?.message ||
        err?.error ||
        err?.details?.error ||
        'Erreur lors du démarrage avec tiroir vide';
      setError(serverMessage);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    const amount = parseFloat(rawAmount || '0');
    if (isNaN(amount) || amount < 0) {
      setError('Veuillez saisir un montant valide');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const notes =
        lastAmount > 0
          ? `Montant reporté de la session précédente: ${formatCurrency(
              lastAmount,
            )} FCFA. Montant ajusté: ${formatCurrency(amount)} FCFA`
          : `Montant initial saisi: ${formatCurrency(amount)} FCFA`;
      await onConfirm(amount, notes);
    } catch (err) {
      const serverMessage =
        err?.message ||
        err?.error ||
        err?.details?.error ||
        'Erreur lors de la création de la session';
      setError(serverMessage);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const numericAmount = rawAmount === '' ? 0 : parseInt(rawAmount, 10) || 0;
  const previousClosing = Number.isFinite(lastAmount)
    ? Math.max(0, Math.round(lastAmount))
    : 0;
  const deltaAmount = numericAmount - previousClosing;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/80 backdrop-blur-sm px-4 py-6">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-x-hidden overflow-y-auto rounded-3xl border border-white/10 bg-gradient-to-br from-gray-900 to-gray-950 shadow-2xl">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm uppercase tracking-wide opacity-70">
                Session de caisse
              </p>
              <h2 className="text-2xl font-bold">Fond de caisse initial</h2>
            </div>
            <div className="rounded-2xl bg-white/20 p-3">
              <Coins className="h-7 w-7" />
            </div>
          </div>
          <p className="mt-3 text-sm text-white/90">
            Indiquez le montant effectivement présent dans votre tiroir avant
            d&apos;ouvrir la session.
          </p>
        </div>

        <div className="space-y-6 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/5 bg-white/5 p-4">
            <div>
              <p className="text-sm text-white/70">Caissier connecté</p>
              <p className="text-lg font-semibold text-white">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs uppercase tracking-wide text-white/60">
                {user?.role}
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm text-white">
              <Clock className="h-4 w-4" />
              <span>{new Date().toLocaleString('fr-FR')}</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-white/5 bg-gray-900/70 p-4">
              <div className="flex items-center justify-between text-sm text-white/70">
                <span>Montant de clôture précédent</span>
                <TrendingUp className="h-5 w-5 text-emerald-400" />
              </div>
              <p className="mt-2 text-2xl font-semibold text-white">
                {formatCurrency(previousClosing)} FCFA
              </p>
              <p className="text-xs text-white/60">
                {previousClosing > 0
                  ? 'Suggestion : dernière clôture.'
                  : 'Aucune session clôturée.'}
              </p>
              <button
                type="button"
                disabled={previousClosing <= 0}
                onClick={() => handlePresetSelect(previousClosing)}
                className="mt-3 inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Utiliser ce montant
              </button>
            </div>
            <div className="rounded-2xl border border-white/5 bg-gray-900/70 p-4">
              <div className="flex items-center justify-between text-sm text-white/70">
                <span>Montant sélectionné</span>
                <Calculator className="h-5 w-5 text-orange-300" />
              </div>
              <p className="mt-2 text-2xl font-semibold text-white">
                {formatCurrency(numericAmount)} FCFA
              </p>
              <p
                className={`text-xs ${
                  deltaAmount === 0
                    ? 'text-white/60'
                    : deltaAmount > 0
                      ? 'text-emerald-400'
                      : 'text-red-400'
                }`}
              >
                {deltaAmount === 0
                  ? 'Identique à la dernière clôture.'
                  : deltaAmount > 0
                    ? `+${formatCurrency(deltaAmount)} FCFA vs la dernière clôture.`
                    : `${formatCurrency(deltaAmount)} FCFA vs la dernière clôture.`}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/5 bg-gray-900/70 p-4">
            <label
              htmlFor="initialAmount"
              className="mb-2 flex items-center gap-2 text-sm font-medium text-white/80"
            >
              <Calculator className="h-4 w-4 text-orange-400" />
              Montant initial à valider
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-3 left-4 text-sm text-white/50">
                FCFA
              </span>
              <input
                id="initialAmount"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={rawAmount}
                onChange={handleAmountChange}
                className={`w-full rounded-2xl border bg-gray-950/80 py-3 pl-16 pr-4 text-right text-lg font-semibold text-white shadow-inner transition focus:border-orange-400 focus:outline-none ${
                  error ? 'border-red-500' : 'border-white/10'
                }`}
                placeholder="0"
                autoFocus
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
              <button
                type="button"
                onClick={() => setShowPreset((prev) => !prev)}
                className="rounded-full border border-orange-400/40 px-4 py-1 text-orange-300 transition hover:bg-orange-500/10"
              >
                {showPreset ? 'Masquer' : 'Montants suggérés'}
              </button>
              <span className="text-white/50">
                Ajustez rapidement votre fond de caisse.
              </span>
            </div>

            {showPreset && (
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handlePresetSelect(amount)}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white transition hover:border-orange-400/60 hover:bg-orange-400/10"
                  >
                    {formatCurrency(amount)} FCFA
                  </button>
                ))}
              </div>
            )}

            {error && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/5 bg-gray-950/80 p-6 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={handleStartWithEmpty}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10 sm:w-auto"
          >
            Commencer sans fond de caisse
          </button>

          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-transparent bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/30 transition hover:brightness-110 sm:w-auto"
          >
            {isLoading ? (
              <span className="animate-pulse">⏳ Création...</span>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Démarrer la session
              </>
            )}
          </button>
        </div>

        <div className="border-t border-white/5 bg-gray-950/50 px-6 py-4 text-sm text-white/70">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-orange-400" />
            <p>
              Ce montant est enregistré comme point de départ pour le
              rapprochement de fin de session.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashDrawerModal;
